import { useState, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { getAuthHeaders, logout } from '../lib/auth';
import {
  Wand2,
  Copy,
  CheckCircle,
  Loader2,
  LogOut,
  User,
  LayoutDashboard,
  BookOpen,
  Download,
  Printer,
  FileUp,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import mammoth from 'mammoth';
import { getDocument, GlobalWorkerOptions } from 'pdfjs-dist';

GlobalWorkerOptions.workerSrc =
  'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function wordCount(text) {
  return text.trim() ? text.trim().split(/\s+/).length : 0;
}

const escHtml = (s) =>
  (s ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/\n/g, '<br>');

// X (Twitter) logo SVG
function XLogo({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.746l7.73-8.835L1.254 2.25H8.08l4.253 5.622zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
    </svg>
  );
}

export default function ContentGeneratorPage() {
  const navigate = useNavigate();

  const [sermonText, setSermonText] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [result, setResult] = useState(null);
  const [activeTab, setActiveTab] = useState('devotional');
  const [error, setError] = useState('');
  const [copiedTweet, setCopiedTweet] = useState(null);
  const [copiedAll, setCopiedAll] = useState(false);
  const [genProgress, setGenProgress] = useState(0);
  const [genLabel, setGenLabel] = useState('');

  const dropRef = useRef(null);
  const progressRef = useRef(null);
  const words = wordCount(sermonText);
  const tooShort = words > 0 && words < 100;

  const handleLogout = () => logout(navigate);

  const startProgress = () => {
    const steps = [
      { at: 0,  label: 'Reading transcript…' },
      { at: 18, label: 'Selecting scriptures…' },
      { at: 42, label: 'Writing content…' },
      { at: 72, label: 'Polishing content…' },
    ];
    let val = 0;
    setGenProgress(0);
    setGenLabel(steps[0].label);
    progressRef.current = setInterval(() => {
      val += 0.7;
      const step = [...steps].reverse().find(s => val >= s.at);
      if (step) setGenLabel(step.label);
      setGenProgress(Math.min(val, 91));
      if (val >= 91) clearInterval(progressRef.current);
    }, 150);
  };

  const stopProgress = (success) => {
    clearInterval(progressRef.current);
    if (success) {
      setGenProgress(100);
      setGenLabel('Done!');
      setTimeout(() => { setGenProgress(0); setGenLabel(''); }, 700);
    } else {
      setGenProgress(0);
      setGenLabel('');
    }
  };

  // ── File extraction ──────────────────────────────────────────────────────
  const extractTextFromFile = async (file) => {
    setError('');
    setExtracting(true);
    try {
      const arrayBuffer = await file.arrayBuffer();
      const isDocx =
        file.name?.endsWith('.docx') ||
        file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
      const isPdf = file.type === 'application/pdf' || file.name?.endsWith('.pdf');

      if (isDocx) {
        const res = await mammoth.extractRawText({ arrayBuffer });
        setSermonText(res.value.trim());
      } else if (isPdf) {
        const pdf = await getDocument({ data: arrayBuffer }).promise;
        let text = '';
        for (let i = 1; i <= pdf.numPages; i++) {
          const page = await pdf.getPage(i);
          const content = await page.getTextContent();
          text += content.items.map((item) => item.str).join(' ') + '\n';
        }
        setSermonText(text.trim());
      } else {
        setError('Unsupported file type. Please drop a .docx or .pdf file.');
      }
    } catch {
      setError('Could not extract text from the file. Please paste the text manually.');
    } finally {
      setExtracting(false);
      setIsDragging(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) extractTextFromFile(file);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    if (!dropRef.current?.contains(e.relatedTarget)) setIsDragging(false);
  };

  // ── Generate ─────────────────────────────────────────────────────────────
  const handleGenerate = async () => {
    setError('');
    if (!sermonText.trim() || words < 100) return;

    setIsGenerating(true);
    setResult(null);
    startProgress();

    try {
      const response = await fetch(`${API}/content/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ sermon_text: sermonText }),
      });

      if (response.ok) {
        stopProgress(true);
        const data = await response.json();
        setResult(data);
        setActiveTab('devotional');
      } else {
        stopProgress(false);
        const err = await response.json();
        setError(err.detail || 'Failed to generate content. Please try again.');
      }
    } catch {
      stopProgress(false);
      setError('Something went wrong. Please check your connection and try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  // ── Copy tweet ─────────────────────────────────────────────────────────
  const handleCopyTweet = async (index, text) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      const el = document.createElement('textarea');
      el.value = text;
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
    }
    setCopiedTweet(index);
    setTimeout(() => setCopiedTweet(null), 2000);
  };

  // ── Copy all tweets ────────────────────────────────────────────────────
  const handleCopyAll = async () => {
    if (!result?.tweets) return;
    const text = result.tweets.map((t, i) => `${i + 1}. ${t}`).join('\n\n');
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      const el = document.createElement('textarea');
      el.value = text;
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
    }
    setCopiedAll(true);
    setTimeout(() => setCopiedAll(false), 2500);
  };

  // ── Download TXT ───────────────────────────────────────────────────────
  const handleDownloadTxt = () => {
    if (!result) return;
    const sep = '═'.repeat(50);
    const lines = [
      `SERMON: ${result.sermonTitle || 'Untitled'}`,
      `Generated by SermonSift — sermonsift.com`,
      '',
      sep,
      '6-DAY DEVOTIONAL SERIES',
      sep,
      '',
      ...(result.dailyWords || []).flatMap((d) => [
        `${d.day.toUpperCase()} — ${d.theme}`,
        `Scripture: ${d.scripture}`,
        `"${d.scriptureText}"`,
        '',
        d.devotional,
        '',
        '─'.repeat(40),
        '',
      ]),
      sep,
      '20 TWEETS',
      sep,
      '',
      ...(result.tweets || []).map((t, i) => `${i + 1}. ${t}`),
    ];

    const blob = new Blob([lines.join('\n')], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const safeTitle = (result.sermonTitle || 'sermon-content')
      .replace(/[^a-z0-9]/gi, '-')
      .toLowerCase();
    a.download = `${safeTitle}-content.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // ── Print HTML (devotional) ────────────────────────────────────────────
  const handlePrintHtml = () => {
    if (!result) return;
    const w = window.open('', '_blank', 'width=900,height=1100');
    w.document.write(`<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <title>${escHtml(result.sermonTitle || '6-Day Devotional')}</title>
  <style>
    *{box-sizing:border-box;margin:0;padding:0}
    @page{margin:0;size:letter}
    body{font-family:Georgia,'Times New Roman',serif;color:#1a1a1a;font-size:11pt;line-height:1.7;background:#fff}
    .doc-header{background:#1B2A4A;padding:28pt 0.85in 24pt}
    .doc-header .eyebrow{font-family:Arial,sans-serif;font-size:7.5pt;letter-spacing:.15em;text-transform:uppercase;color:rgba(255,255,255,.55);margin-bottom:6pt}
    .doc-header h1{font-size:22pt;font-weight:bold;color:#fff;line-height:1.2}
    .doc-header .sub{font-family:Arial,sans-serif;font-size:9pt;color:rgba(255,255,255,.55);margin-top:5pt}
    .body{padding:28pt 0.85in 0.85in}
    .day-card{margin-bottom:24pt;page-break-inside:avoid;border:1px solid #e5e7eb;border-radius:6px;overflow:hidden}
    .day-header{background:#1B2A4A;padding:10pt 14pt;display:flex;align-items:baseline;gap:12pt}
    .day-label{font-family:Arial,sans-serif;font-size:10pt;font-weight:700;color:#C9A227;text-transform:uppercase;letter-spacing:.08em}
    .day-theme{font-family:Arial,sans-serif;font-size:8.5pt;color:rgba(255,255,255,.6);font-style:italic}
    .day-body{padding:12pt 14pt}
    .scripture-ref{font-family:Arial,sans-serif;font-size:8pt;font-weight:700;color:#C9A227;text-transform:uppercase;letter-spacing:.08em;margin-bottom:4pt}
    .scripture-text{font-style:italic;color:#444;font-size:10.5pt;line-height:1.5;margin-bottom:10pt;padding-left:10pt;border-left:3px solid #C9A227}
    .devotional{color:#1a1a1a;font-size:11pt;line-height:1.75}
    footer{margin-top:32pt;padding-top:10pt;border-top:1px solid #ddd;text-align:center;font-family:Arial,sans-serif;font-size:7.5pt;color:#bbb;letter-spacing:.05em}
  </style>
</head>
<body>
<div class="doc-header">
  <div class="eyebrow">6-Day Devotional Series</div>
  <h1>${escHtml(result.sermonTitle || 'Daily Devotionals')}</h1>
  <div class="sub">Generated by SermonSift &nbsp;&middot;&nbsp; sermonsift.com</div>
</div>
<div class="body">
  ${(result.dailyWords || []).map((d) => `
  <div class="day-card">
    <div class="day-header">
      <span class="day-label">${escHtml(d.day)}</span>
      <span class="day-theme">${escHtml(d.theme)}</span>
    </div>
    <div class="day-body">
      <div class="scripture-ref">${escHtml(d.scripture)}</div>
      <div class="scripture-text">&ldquo;${escHtml(d.scriptureText)}&rdquo;</div>
      <div class="devotional">${escHtml(d.devotional)}</div>
    </div>
  </div>`).join('')}
  <footer>Generated by SermonSift &nbsp;&middot;&nbsp; sermonsift.com</footer>
</div>
</body>
</html>`);
    w.document.close();
    w.focus();
    setTimeout(() => w.print(), 600);
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-[#0d1b2e]">
      {/* Navbar */}
      <header className="sticky top-0 z-50 bg-[#0d1b2e]/95 backdrop-blur border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-6">
            <Link to="/" className="font-serif text-xl font-bold text-white">SermonSift</Link>
            <Link
              to="/dashboard"
              className="hidden sm:flex items-center gap-2 text-sm text-blue-300/70 hover:text-white transition-colors"
            >
              <LayoutDashboard className="w-4 h-4" /> Dashboard
            </Link>
          </div>
          <div className="flex items-center gap-3">
            <span className="hidden sm:flex items-center gap-2 text-sm font-medium text-[#c9a227]">
              <Wand2 className="w-4 h-4" /> Content Generator
            </span>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="p-2 rounded-lg hover:bg-white/10 transition-colors">
                  <User className="w-5 h-5 text-blue-300/70" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={handleLogout}>
                  <LogOut className="w-4 h-4 mr-2" /> Sign Out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        {/* Page header */}
        <div className="mb-8 flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-[#c9a227]/20 flex items-center justify-center flex-shrink-0">
            <Wand2 className="w-5 h-5 text-[#c9a227]" strokeWidth={1.5} />
          </div>
          <div>
            <h1 className="text-xl font-serif font-bold text-white leading-none">Content Generator</h1>
            <p className="text-blue-300/50 text-xs mt-0.5">
              Paste a sermon transcript → generate a 6-day devotional series and 20 social posts
            </p>
          </div>
        </div>

        {/* Input panel */}
        <div className="bg-[#1a2f4e] rounded-2xl p-5 mb-8 border border-white/10">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-blue-200/80">Sermon Transcript</span>
            <span className={`text-xs tabular-nums ${tooShort ? 'text-amber-400' : 'text-blue-300/40'}`}>
              {words.toLocaleString()} words
            </span>
          </div>

          <div
            ref={dropRef}
            className="relative"
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
          >
            {isDragging && (
              <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed border-[#c9a227] bg-[#c9a227]/10 pointer-events-none">
                <FileUp className="w-8 h-8 text-[#c9a227]" />
                <p className="text-sm font-semibold text-[#c9a227]">Drop to extract text</p>
                <p className="text-xs text-[#c9a227]/70">.docx or .pdf</p>
              </div>
            )}
            {extracting && (
              <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-2 rounded-lg bg-[#0d1b2e]/80 backdrop-blur-sm">
                <Loader2 className="w-6 h-6 text-[#c9a227] animate-spin" />
                <p className="text-sm text-[#c9a227]">Extracting text…</p>
              </div>
            )}
            <textarea
              value={sermonText}
              onChange={(e) => setSermonText(e.target.value)}
              placeholder="Paste the full sermon transcript here… or drop a .docx / .pdf file"
              className="w-full bg-[#0d1b2e] text-blue-100 placeholder-blue-300/25 rounded-lg p-4 min-h-[180px] resize-y border border-white/10 focus:outline-none focus:border-[#c9a227]/50 transition-colors text-sm leading-relaxed"
            />
          </div>

          {tooShort && (
            <p className="mt-2 text-xs text-amber-400 flex items-center gap-1">
              ⚠ Paste at least 100 words for best results.
            </p>
          )}

          {error && (
            <p className="mt-2 text-xs text-red-400">{error}</p>
          )}

          <div className="flex justify-end mt-3">
            <button
              onClick={handleGenerate}
              disabled={isGenerating || words < 100}
              className="flex items-center gap-2 px-6 py-2.5 rounded-xl font-semibold text-sm transition-all disabled:opacity-40 disabled:cursor-not-allowed"
              style={{ background: isGenerating || words < 100 ? '#2d4a6e' : 'linear-gradient(135deg, #c9a227 0%, #e6c04a 100%)', color: isGenerating || words < 100 ? '#6b8aad' : '#1B2A4A' }}
            >
              {isGenerating ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Generating…
                </>
              ) : (
                <>
                  <Wand2 className="w-4 h-4" />
                  Generate Content
                </>
              )}
            </button>
          </div>

          {isGenerating && (
            <div className="mt-4">
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-xs text-blue-300/50">{genLabel}</span>
                <span className="text-xs font-bold tabular-nums text-[#c9a227]">{Math.round(genProgress)}%</span>
              </div>
              <div className="h-1.5 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.08)' }}>
                <div
                  className="h-full rounded-full transition-all duration-300 ease-out"
                  style={{ width: `${genProgress}%`, background: 'linear-gradient(90deg, #c9a227, #e6c04a)' }}
                />
              </div>
            </div>
          )}
        </div>

        {/* Results */}
        {result && (
          <div>
            {/* Sermon title */}
            {result.sermonTitle && (
              <p className="text-blue-300/50 text-xs mb-4 text-center">
                Based on: <span className="text-blue-200/70 italic">"{result.sermonTitle}"</span>
              </p>
            )}

            {/* Tab switcher */}
            <div className="flex gap-1 mb-6 border-b border-white/10">
              <button
                onClick={() => setActiveTab('devotional')}
                className={`flex items-center gap-2 px-5 py-3 text-sm font-medium border-b-2 transition-colors -mb-px ${
                  activeTab === 'devotional'
                    ? 'text-[#c9a227] border-[#c9a227]'
                    : 'text-blue-300/40 border-transparent hover:text-blue-300/70'
                }`}
              >
                <BookOpen className="w-4 h-4" />
                6-Day Devotional
              </button>
              <button
                onClick={() => setActiveTab('tweets')}
                className={`flex items-center gap-2 px-5 py-3 text-sm font-medium border-b-2 transition-colors -mb-px ${
                  activeTab === 'tweets'
                    ? 'text-[#4d9de0] border-[#4d9de0]'
                    : 'text-blue-300/40 border-transparent hover:text-blue-300/70'
                }`}
              >
                <XLogo size={14} />
                20 Tweets
              </button>
            </div>

            {/* Devotional tab */}
            {activeTab === 'devotional' && (
              <div>
                <div className="flex gap-3 mb-5">
                  <button
                    onClick={handleDownloadTxt}
                    className="flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-medium bg-[#1a2f4e] text-blue-200/70 hover:text-white border border-white/10 hover:border-white/20 transition-colors"
                  >
                    <Download className="w-3.5 h-3.5" />
                    Download TXT
                  </button>
                  <button
                    onClick={handlePrintHtml}
                    className="flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-medium bg-[#1a2f4e] text-blue-200/70 hover:text-white border border-white/10 hover:border-white/20 transition-colors"
                  >
                    <Printer className="w-3.5 h-3.5" />
                    Print / Save PDF
                  </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {(result.dailyWords || []).map((d) => (
                    <div
                      key={d.day}
                      className="bg-[#1a2f4e] rounded-xl overflow-hidden border border-white/10"
                    >
                      <div className="bg-[#0d1b2e] px-4 py-3 flex items-baseline gap-3 border-b border-white/10">
                        <span className="text-[#c9a227] font-bold text-xs uppercase tracking-wider">
                          {d.day}
                        </span>
                        <span className="text-blue-300/40 text-xs italic truncate">{d.theme}</span>
                      </div>
                      <div className="p-4">
                        <div className="text-[#c9a227]/80 text-xs font-semibold mb-1">{d.scripture}</div>
                        <p className="text-blue-200/50 text-xs italic mb-3 leading-relaxed border-l-2 border-[#c9a227]/30 pl-2">
                          "{d.scriptureText}"
                        </p>
                        <p className="text-blue-100/75 text-sm leading-relaxed">{d.devotional}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Tweets tab */}
            {activeTab === 'tweets' && (
              <div>
                <div className="flex items-center justify-between mb-5">
                  <a
                    href="https://x.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-xs text-[#4d9de0] hover:text-blue-300 transition-colors"
                  >
                    <XLogo size={13} />
                    Open X / Twitter
                  </a>
                  <button
                    onClick={handleCopyAll}
                    className="flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-medium bg-[#1a2f4e] border border-white/10 hover:border-[#4d9de0]/40 transition-colors"
                    style={{ color: copiedAll ? '#4ade80' : '#4d9de0' }}
                  >
                    {copiedAll ? (
                      <><CheckCircle className="w-3.5 h-3.5" /> Copied!</>
                    ) : (
                      <><Copy className="w-3.5 h-3.5" /> Copy All 20</>
                    )}
                  </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {(result.tweets || []).map((tweet, i) => (
                    <div
                      key={i}
                      className="bg-[#1a2f4e] rounded-xl p-4 border border-white/10 flex gap-3"
                    >
                      <span className="text-blue-300/25 text-xs font-mono w-5 flex-shrink-0 pt-0.5">
                        {String(i + 1).padStart(2, '0')}
                      </span>
                      <p className="text-blue-100/80 text-sm leading-relaxed flex-1">{tweet}</p>
                      <div className="flex flex-col gap-1.5 flex-shrink-0">
                        <button
                          onClick={() => handleCopyTweet(i, tweet)}
                          title="Copy tweet"
                          className="p-1.5 rounded-lg hover:bg-white/10 transition-colors"
                          style={{ color: copiedTweet === i ? '#4ade80' : '#4d9de0' }}
                        >
                          {copiedTweet === i ? (
                            <CheckCircle className="w-4 h-4" />
                          ) : (
                            <Copy className="w-4 h-4" />
                          )}
                        </button>
                        <a
                          href={`https://x.com/intent/tweet?text=${encodeURIComponent(tweet)}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          title="Post to X"
                          className="p-1.5 rounded-lg hover:bg-white/10 transition-colors text-blue-300/50 hover:text-white"
                        >
                          <XLogo size={14} />
                        </a>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
}
